import { observable } from "mobx";
import { DynamoNode, ElementNode, InputNode, NodeTag } from ".";
import { TemplateNodeVisitor } from "./ops/nodeVisitor";
import { ElementType, FormType } from "../enums";

export class FormNode extends DynamoNode {
    public readonly tag: NodeTag = NodeTag.Form;

    public primId?: number;

    public clientId?: number;

    @observable public name?: string;

    @observable public formType: FormType = FormType.Invalid;

    @observable public children: PageNode[] = [];

    public tryAddChild(child: DynamoNode, index?: number) {
        if (child.tag !== NodeTag.Page) {
            return false;
        }

        if (index !== undefined) {
            this.children = [...this.children.slice(0, index), child as PageNode, ...this.children.slice(index)];
        } else {
            this.children = [...this.children, child as PageNode];
        }

        return true;
    }

    public tryRemoveChild(child: DynamoNode) {
        const origLength = this.children.length;
        this.children = this.children.filter((v) => v.nodeId !== child.nodeId);

        return this.children.length !== origLength;
    }

    public getChildren(): DynamoNode[] {
        return this.children.map((v) => v as DynamoNode);
    }

    @observable public validateSubmission(shouldShortCircuit: boolean): boolean {
        return this.children.map((v) => v.validateSubmission(shouldShortCircuit)).every((v) => v);
    }

    public accept(visitor: TemplateNodeVisitor) {
        visitor.visitFormNode(this);
    }
}

export class PageNode extends DynamoNode {
    public readonly tag: NodeTag = NodeTag.Page;

    public primId?: number;

    @observable public name?: string;

    @observable public children: ElementNode[] = [];

    public tryAddChild(child: DynamoNode, index?: number | undefined) {
        if (child.tag !== NodeTag.Element) {
            return false;
        }

        if (index !== undefined) {
            this.children = [...this.children.slice(0, index), child as ElementNode, ...this.children.slice(index)];
        } else {
            this.children = [...this.children, child as ElementNode];
        }

        return true;
    }

    public tryRemoveChild(child: DynamoNode) {
        const origLength = this.children.length;
        this.children = this.children.filter((v) => v.nodeId !== child.nodeId);

        return this.children.length !== origLength;
    }

    public getChildren(): DynamoNode[] {
        return this.children.map((v) => v as DynamoNode);
    }

    public validateSubmission(shouldShortCircuit: boolean): boolean {
        return this.children.map((v) => v.validateSubmission(shouldShortCircuit)).every((v) => v);
    }

    public accept(visitor: TemplateNodeVisitor) {
        visitor.visitPageNode(this);
    }
}

export class SectionNode extends ElementNode {
    public constructor() {
        super();
        this.elementType = ElementType.Section;
    }

    public accept(visitor: TemplateNodeVisitor) {
        visitor.visitSectionNode(this);
    }
}

export class TableNode extends ElementNode {
    public constructor() {
        super();
        this.elementType = ElementType.Table;
    }

    public accept(visitor: TemplateNodeVisitor) {
        visitor.visitTableNode(this);
    }

    public tryAddChild(child: DynamoNode, index?: number | undefined) {
        if (child.tag !== NodeTag.Element) {
            return false;
        }

        const elemNode = child as ElementNode;

        if (this.children.includes(elemNode)) {
            return false;
        }

        if (elemNode === undefined) {
            return false;
        }

        if (elemNode.elementType === ElementType.Row) {
            return super.tryAddChild(elemNode, index);
        }

        const rowNode = new RowNode();

        return rowNode.tryAddChild(elemNode) && super.tryAddChild(rowNode);
    }
}

export class RowNode extends ElementNode {
    public constructor() {
        super();
        this.elementType = ElementType.Row;
    }

    public accept(visitor: TemplateNodeVisitor) {
        visitor.visitRowNode(this);
    }

    public tryAddChild(child: DynamoNode, index?: number | undefined) {
        if (child.tag !== NodeTag.Element) {
            return false;
        }

        const elemNode = child as ElementNode;

        if (elemNode === this || this.children.includes(elemNode)) {
            return false;
        }

        if (elemNode === undefined) {
            return false;
        }

        if (elemNode.elementType === ElementType.Cell) {
            return super.tryAddChild(elemNode, index);
        }

        const cellNode = new CellNode();

        return cellNode.tryAddChild(elemNode) && super.tryAddChild(cellNode, index);
    }
}

export class CellNode extends ElementNode {
    public constructor() {
        super();
        this.elementType = ElementType.Cell;
    }

    public accept(visitor: TemplateNodeVisitor) {
        visitor.visitCellNode(this);
    }
}
